/** Which editors the tray offers, and which of them apply to what is selected (`WEB-012`). */

import type { AddedActivityRow } from "../../types";

/** What the tray is editing: the space itself, or one activity added to it. */
export type Subject =
  | { readonly kind: "space" }
  | {
      readonly kind: "activity";
      readonly row: AddedActivityRow;
      /** Whether its pinned manifest is a bundle, which is what Composition edits. */
      readonly bundle: boolean;
    };

/** Which editor is open. A flat set of identifiers, extended by adding one entry below. */
export type PanelId =
  | "space"
  | "activity"
  | "layers"
  | "bindings"
  | "access"
  | "canvases"
  | "needs"
  | "composition"
  | "ability"
  | "state"
  | "cues"
  | "catalog"
  | "history";

/** Which subjects a panel edits. A panel is offered only for a subject it can actually edit. */
type Applies = "space" | "activity" | "either" | "bundle";

/** How the column groups its icons, in this order: identity, library, arrangement, wiring,
    observation. A separator falls wherever one group ends and the next begins. */
export type PanelGroup = "identity" | "library" | "arrangement" | "wiring" | "observation";

export interface PanelEntry {
  readonly id: PanelId;
  /** The accessible name of its button in the column, and the heading over its body. */
  readonly label: string;
  /** The one character that opens it. Fixed per panel, so a key never means two things. */
  readonly key: string;
  readonly applies: Applies;
  /** Composition needs the tray's wide width: a tree and a full pane will not share 360 px. */
  readonly wide?: boolean;
  readonly group: PanelGroup;
  /** Cues reads cue bindings out of a live scene document and nothing else names one. */
  readonly requiresLiveScene?: boolean;
}

/** The column, grouped, its key written on its button. Group order is fixed; so is a panel's
    key, which never moves even though this array's order now does. */
export const PANELS: readonly PanelEntry[] = [
  { id: "space", label: "Space", key: "1", applies: "space", group: "identity" },
  { id: "activity", label: "Activity", key: "2", applies: "activity", group: "identity" },
  { id: "catalog", label: "Catalog", key: "C", applies: "either", group: "library" },
  { id: "layers", label: "Layers", key: "3", applies: "either", group: "arrangement" },
  { id: "canvases", label: "Canvases", key: "6", applies: "either", group: "arrangement" },
  { id: "bindings", label: "Bindings", key: "4", applies: "either", group: "wiring" },
  { id: "needs", label: "Needs", key: "7", applies: "either", group: "wiring" },
  {
    id: "composition",
    label: "Composition",
    key: "8",
    applies: "bundle",
    wide: true,
    group: "wiring",
  },
  { id: "access", label: "Access", key: "5", applies: "either", group: "wiring" },
  // E for the ability system, beside State: State is what is held right now, this is what may be.
  { id: "ability", label: "Ability system", key: "E", applies: "either", group: "observation" },
  { id: "state", label: "State", key: "9", applies: "either", group: "observation" },
  {
    id: "cues",
    label: "Cues",
    key: "0",
    applies: "either",
    group: "observation",
    requiresLiveScene: true,
  },
  // R, not H: section D of the design gives H to hide-the-selected-layer, which is a stage
  // gesture used constantly while arranging. A panel key loses that contest.
  { id: "history", label: "History", key: "R", applies: "either", group: "observation" },
];

/** Whether a spelling off the address bar names a panel this build ships. */
export function isPanelId(value: string | undefined): value is PanelId {
  return value !== undefined && PANELS.some((one) => one.id === value);
}

function fits(entry: PanelEntry, subject: Subject, hasLiveScene: boolean): boolean {
  if (entry.requiresLiveScene === true && !hasLiveScene) return false;
  if (entry.applies === "either") return true;
  if (entry.applies === "bundle") return subject.kind === "activity" && subject.bundle;
  return entry.applies === subject.kind;
}

/**
 * The panels this subject is offered, in column order. A panel it cannot edit is not listed,
 * and `hasLiveScene` keeps Cues off the column until at least one live mount holds a scene.
 */
export function offeredPanels(subject: Subject, hasLiveScene = false): PanelEntry[] {
  return PANELS.filter((one) => fits(one, subject, hasLiveScene));
}

/** The panel one key opens for this subject, or undefined when that key opens nothing here. */
export function panelForKey(
  key: string,
  subject: Subject,
  hasLiveScene = false,
): PanelId | undefined {
  const wanted = key.toLowerCase();
  return offeredPanels(subject, hasLiveScene).find((one) => one.key.toLowerCase() === wanted)?.id;
}

/** Whether this panel takes the tray to its wide width. */
export function isWidePanel(panel: PanelId): boolean {
  return PANELS.find((one) => one.id === panel)?.wide === true;
}

/** The panel the tray draws: the asked-for one, or the subject's own if it is not offered. */
export function shownPanel(
  asked: PanelId | undefined,
  subject: Subject,
  hasLiveScene = false,
): PanelId {
  const offered = offeredPanels(subject, hasLiveScene);
  if (asked !== undefined && offered.some((one) => one.id === asked)) return asked;
  return offered[0]?.id ?? "space";
}
